import { ActionSettings, ToolbarButton, TableDataRequest } from '@fl/cmsch-fe-library';
import React, { useCallback, useMemo, memo, FC } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Opt } from 'ts-opt';
import { MaleHolsteinAnimalInList } from 'api/gen/MaleHolsteinAnimalInList';
import { useOurTranslation } from 'app/translations';
import { ConnectedTable } from 'utils/tables';
import { matingHolsteinBullsColumns } from './mating-holstein-bulls-columns';
import { useTableSettingsExists } from '../../hooks/use-table-settings-exists';
import { matingAction, matingSelector } from '../../model';
const pathnamesForRefresh: Array<RegExp> = [/^\/mating-h/, /^\/bullselector-h/];
interface Props {
  /** Whether the export action is currently running. */
  exportInProgress: boolean;
  /** Removes one bull from the table. */
  removeBull(animalId: number): void;
  /** Exports the current bull-selector result. */
  exportBullselectorResult(): void;
}
const MatingBullsHolsteinTableBase: FC<Props> = ({
  exportInProgress,
  removeBull,
  exportBullselectorResult
}) => {
  const dispatch = useDispatch();
  const columnSettingsIsEmpty = !useTableSettingsExists('MATING_H', 'BULL');
  const getMatingBulls = useCallback((tableDataRequest: Opt<TableDataRequest<MaleHolsteinAnimalInList>>) => {
    dispatch(matingAction.getHolsteinMatingBulls(tableDataRequest));
  }, [dispatch]);
  const {
    t
  } = useOurTranslation('common');
  const isPageEmpty = useSelector(matingSelector.isHolsteinPageEmpty);
  const actionSettings: ActionSettings<MaleHolsteinAnimalInList> = useMemo(() => ({
    onRowRemove: removeBull
  }), [removeBull]);
  const headerButtons: Array<ToolbarButton> = useMemo(() => [{
    id: 'exportCows',
    title: t('downloadCsv'),
    onClick: exportBullselectorResult,
    disabled: isPageEmpty,
    role: 'export',
    loading: exportInProgress
  }], [exportBullselectorResult, exportInProgress, isPageEmpty, t]);
  return <ConnectedTable tableId="matingHolsteinBulls" columns={columnSettingsIsEmpty ? [] : matingHolsteinBullsColumns} onFetchTableData={getMatingBulls} rowDataPageSelector={matingSelector.maleHolsteinAnimalsPage} actionSettings={actionSettings} withoutVerticalScrollbar showFulltextFilter paginationPosition="both" headerButtons={headerButtons} enableTableUserSettings pathnamesForRefresh={pathnamesForRefresh} data-sentry-element="ConnectedTable" data-sentry-component="MatingBullsHolsteinTableBase" data-sentry-source-file="mating-bulls-holstein-table.tsx" />;
};

/**
 * Memoized Holstein bull table used by the mating workflow.
 *
 * @param props Table actions and loading state.
 * @returns The Holstein bull table.
 *
 * @example
 * <MatingBullsHolsteinTable
 *   exportInProgress={false}
 *   removeBull={handleRemoveBull}
 *   exportBullselectorResult={handleExport}
 * />
 */
export const MatingBullsHolsteinTable = memo(MatingBullsHolsteinTableBase);